import { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import PropTypes from "prop-types";

import ActionButton from "../../formInputs/buttons/ActionButton";
import CommentModal from "../shared/CommentModal";
import DiffCellCommentIcon from "./DiffCellCommentIcon";
import isGradingProfileWindowOpen from "../../../utils/ActionControls/isGradingProfileWindowOpen";
import SafeMathHtml from "../../shared/SafeMathHtml";
import SortHeader from "../../shared/SortHeader";
import StudentImage from "../../shared/StudentImage";
import useScrollSync from "../../../hooks/useScrollSync";
import useSortConfig from "../../../hooks/useSortConfig";
import useStudentImages from "../../../hooks/useStudentImages";

import { compareGroupNameColumn, groupNameNormalizer } from "../../../utils/GroupNameNormalizer";
import { getColorClass, RATING_DISPLAY_LABELS, RATING_OPTIONS } from "../../../utils/teacher/InputRatingUtils";
import { isDiffStudentEditable } from "../../../hooks/ratings/diff/isDiffStudentEditable";
import { cellKey, findDiffRating } from "../../../hooks/ratings/diff/indexRosterDiffRatings";

import "../../../styles/RatingTable.scss";

const DiffRatingGrid = ({
    actionWindowsDto,
    basePath,
    groupData,
    hasChanges,
    items,
    comments,
    mode,
    onBulkRating,
    onRatingChange,
    onSave,
    onSaveComment,
    onSaveSubjectComments,
    onSubjectCommentChange,
    ratings,
    ratingsLoaded,
    selectedEpr,
    selectedSchoolLocationKey,
    selectedTemplate,
    snapshots,
    students,
    subjectComments,
    token,
    userDetails
}) => {
    const navigate = useNavigate();
    const [activeColumnIndex, setActiveColumnIndex] = useState(0);
    const [showComments, setShowComments] = useState(false);
    const [commentModalOpen, setCommentModalOpen] = useState(false);
    const [activeCommentContext, setActiveCommentContext] = useState(null);
    const { sortConfig, requestSort } = useSortConfig(mode === "GROUPS" ? "groupName" : "lastName");

    const fixedRef = useRef(null);
    const scrollRef = useRef(null);
    const columnRefs = useRef([]);

    const timePeriod = mode === "GROUPS" ? groupData?.[0]?.timePeriod : selectedEpr?.timePeriod;
    const isActiveWindow = isGradingProfileWindowOpen(actionWindowsDto, timePeriod);
    const schoolYearKey = selectedEpr?.schoolYearKey ?? groupData?.[0]?.schoolYearKey;

    const studentEditable = useCallback(
        (student) => isDiffStudentEditable({ student, mode, isActiveWindow, selectedEpr, userDetails }),
        [mode, isActiveWindow, selectedEpr, userDetails]
    );
    const anyStudentEditable = (students || []).some(studentEditable);
    // Same legacy rule as on-level RatingGrid: a VIEW group in the selection
    // disables Rate for All even when other students remain editable.
    const viewOnlyGroupExists = groupData?.some((g) => g.permission === "VIEW");
    const bulkRatingDisabled = !anyStudentEditable || !ratingsLoaded || viewOnlyGroupExists;

    const sortedStudents = useMemo(() => {
        const list = [...(students || [])];
        return list.sort((a, b) => {
            if (mode === "GROUPS" && sortConfig.key === "groupName") {
                return compareGroupNameColumn(a, b, sortConfig.direction);
            }
            const aVal = a[sortConfig.key]?.toLowerCase?.() || "";
            const bVal = b[sortConfig.key]?.toLowerCase?.() || "";
            return sortConfig.direction === "ascending" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
    }, [students, mode, sortConfig]);

    const { images: studentImages, loading: imagesLoading } = useStudentImages(sortedStudents, schoolYearKey, token);

    useScrollSync(fixedRef, scrollRef, [imagesLoading]);

    const itemIds = items.map((item) => `${item.grade}|${item.contentId}`).join(",");
    useEffect(() => {
        columnRefs.current = items.map(() => createRef());
        setActiveColumnIndex(0);
        // Reset tabs only when the column set changes, not on post-save refresh.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemIds]);

    useEffect(() => {
        const el = columnRefs.current[activeColumnIndex]?.current;
        const container = scrollRef.current;
        if (!el || !container) return;
        const left = el.offsetLeft - container.offsetLeft;
        const right = left + el.offsetWidth;
        if (left < container.scrollLeft) {
            container.scrollTo({ left: left - 20, behavior: "smooth" });
        } else if (right > container.scrollLeft + container.clientWidth) {
            container.scrollTo({ left: right - container.clientWidth + 20, behavior: "smooth" });
        }
    }, [activeColumnIndex]);

    const handleOpenStudent = useCallback(
        (student) => {
            const preselectedGroupIds = Array.isArray(groupData) ? groupData.map((r) => String(r.groupId)) : [];
            navigate(
                {
                    pathname: `${basePath || "/home"}/summary/input-ratings/student/${student.studentNumber}`,
                    search: selectedTemplate?.templateId ? `?templateId=${selectedTemplate.templateId}` : ""
                },
                {
                    state: {
                        mode,
                        sharedGroupId: student.groupId,
                        preselectedGroupIds,
                        selectedSchoolLocationKey
                    }
                }
            );
        },
        [basePath, groupData, mode, navigate, selectedSchoolLocationKey, selectedTemplate]
    );

    const closeCommentModal = useCallback(() => {
        setCommentModalOpen(false);
        setActiveCommentContext(null);
    }, []);

    const handleToggleComments = useCallback(async () => {
        if (hasChanges) await onSave();
        if (showComments && onSaveSubjectComments) await onSaveSubjectComments();
        setShowComments((prev) => !prev);
    }, [hasChanges, onSave, onSaveSubjectComments, showComments]);

    const openCommentModal = useCallback(
        (student, item) => {
            const key = cellKey(student.studentKey, item.grade, item.contentId);
            const diffRow = findDiffRating(snapshots?.[student.studentKey], item.grade, item.contentId);
            if (!diffRow) return;
            setActiveCommentContext({
                student: {
                    ...student,
                    isDisabled: !studentEditable(student),
                    contentRatings: [{ key: String(diffRow.key), comment: diffRow.comment ?? null }]
                },
                contentItem: { label: item.label || item.header || String(item.contentId) },
                rating: ratings[key] || "",
                comment: comments?.[key] || "",
                key: String(diffRow.key),
                studentKey: student.studentKey,
                grade: item.grade,
                contentId: item.contentId
            });
            setCommentModalOpen(true);
        },
        [comments, ratings, snapshots, studentEditable]
    );

    if (!students.length) {
        return <p>No students have differentiated items at this grade.</p>;
    }

    const activeItem = items[activeColumnIndex] || {};
    const preview = (item) => item.header || item.label || item.contentId;

    return (
        <section>
            <div className={classNames("rating-column-description-wrapper", { hide: showComments })}>
                <h5 className="rating-column-desc-title">Selected Content Item Description:</h5>
                <div className="rating-column-description">
                    {activeItem.standardName && activeItem.contentType !== "CDESTANDARD" && (
                        <div className="hierarchy-text-wrapper">
                            <span className="hierarchy-text">Standard:</span>
                            <span dangerouslySetInnerHTML={{ __html: activeItem.standardName }} />
                        </div>
                    )}
                    {activeItem.gleName && activeItem.contentType !== "GLE" && (
                        <div className="hierarchy-text-wrapper">
                            <span style={{ marginRight: 8, fontWeight: 600, marginBottom: 4 }}>GLE:</span>
                            <span dangerouslySetInnerHTML={{ __html: activeItem.gleName }} />
                        </div>
                    )}
                    {activeItem.contentType === "EVIDENCE_OUTCOME_CHILD" && activeItem.eoName && (
                        <div className="hierarchy-text-wrapper">
                            <span className="hierarchy-text">Parent Evidence Outcome:</span>
                            <span dangerouslySetInnerHTML={{ __html: activeItem.eoName }} />
                        </div>
                    )}
                    <hr />
                    <div>
                        <strong>Selected Content Item:</strong>
                        <SafeMathHtml
                            as="p"
                            cacheKey={`diff-label-${activeItem.grade}-${activeItem.contentId}`}
                            html={activeItem.label || preview(activeItem)}
                        />
                    </div>
                </div>
            </div>

            <div className="rating-table-btn-group">
                <ActionButton
                    label={showComments ? "Hide Student Comments" : "Show Student Comments"}
                    onClick={handleToggleComments}
                />
                <div className="column-nav">
                    <ActionButton
                        className="action-button-80"
                        disable={showComments || activeColumnIndex === 0}
                        label="← Prev"
                        onClick={() => setActiveColumnIndex((i) => Math.max(0, i - 1))}
                    />
                    <ActionButton
                        className="action-button-80"
                        disable={showComments || activeColumnIndex >= items.length - 1}
                        label="Next →"
                        onClick={() => setActiveColumnIndex((i) => Math.min(items.length - 1, i + 1))}
                    />
                </div>
            </div>

            <div className="ratings-table-wrapper">
                <div className="table-split-wrapper">
                    <div className="fixed-columns" ref={fixedRef}>
                        <table className="rating-table">
                            <thead>
                                <tr>
                                    {mode === "GROUPS" && (
                                        <th>
                                            <SortHeader
                                                label="Group Name"
                                                columnKey="groupName"
                                                sortConfig={sortConfig}
                                                onSort={requestSort}
                                            />
                                        </th>
                                    )}
                                    <th />
                                    <th>
                                        <SortHeader
                                            label="Last Name"
                                            columnKey="lastName"
                                            sortConfig={sortConfig}
                                            onSort={requestSort}
                                        />
                                    </th>
                                    <th>
                                        <SortHeader
                                            label="First Name"
                                            columnKey="firstName"
                                            sortConfig={sortConfig}
                                            onSort={requestSort}
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedStudents.map((student) => (
                                    <tr
                                        key={student.studentKey}
                                        className={
                                            student.isShared && student.groupPermission === "VIEW"
                                                ? "view-only-row"
                                                : ""
                                        }
                                    >
                                        {mode === "GROUPS" && (
                                            <td>
                                                <div className="group-cell">
                                                    <b>{groupNameNormalizer(student.groupName)}</b>{" "}
                                                    {student.isShared && (
                                                        <div className="group-text">{student.groupHomeroomTeacher}</div>
                                                    )}
                                                    {student.isShared && student.groupPermission === "VIEW" && (
                                                        <div className="group-text view-text">View Only</div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        <td>
                                            <StudentImage
                                                src={studentImages[student.studentNumber]}
                                                firstName={student.firstName}
                                                lastName={student.lastName}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="as-link"
                                                onClick={() => handleOpenStudent(student)}
                                            >
                                                {student.lastName}
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="as-link"
                                                onClick={() => handleOpenStudent(student)}
                                            >
                                                {student.firstName}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="scrollable-columns" ref={scrollRef}>
                        <table className="rating-table" style={showComments ? { width: "100%" } : {}}>
                            <thead>
                                {showComments ? (
                                    <tr>
                                        <th style={{ fontSize: "1rem" }}>Comments</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        {items.map((item, index) => (
                                            <th
                                                key={`${item.grade}-${item.contentId}`}
                                                className={index === activeColumnIndex ? "active-rating-col" : ""}
                                                ref={columnRefs.current[index]}
                                            >
                                                <button
                                                    type="button"
                                                    className="clickable-header"
                                                    onClick={() => setActiveColumnIndex(index)}
                                                >
                                                    <SafeMathHtml
                                                        cacheKey={`diff-hdr-${item.grade}-${item.contentId}`}
                                                        html={preview(item)}
                                                    />
                                                </button>
                                                <select
                                                    aria-label={`Bulk Rating for ${preview(item)}`}
                                                    disabled={bulkRatingDisabled}
                                                    value=""
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        onBulkRating(
                                                            item.grade,
                                                            item.contentId,
                                                            val === "__CLEAR__" ? null : val || null
                                                        );
                                                    }}
                                                >
                                                    <option value="">Rate for All</option>
                                                    {RATING_OPTIONS.map((opt) => (
                                                        <option key={opt} value={opt}>
                                                            {RATING_DISPLAY_LABELS[opt] || opt}
                                                        </option>
                                                    ))}
                                                    <option value="__CLEAR__">Clear Ratings</option>
                                                </select>
                                            </th>
                                        ))}
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                {showComments
                                    ? sortedStudents.map((student) => {
                                          const canEdit = studentEditable(student);
                                          const commentKey = `${student.studentKey}_${student.gradingCriteriaId}`;
                                          return (
                                              <tr key={student.studentKey}>
                                                  <td colSpan={items.length || 1}>
                                                      <textarea
                                                          aria-label={`Subject comment for ${student.firstName} ${student.lastName}`}
                                                          className="student-criteria-comment-input"
                                                          disabled={!canEdit}
                                                          style={{ width: "100%" }}
                                                          value={subjectComments?.[commentKey] || ""}
                                                          onChange={(e) =>
                                                              onSubjectCommentChange?.(
                                                                  student.studentKey,
                                                                  e.target.value,
                                                                  student.gradingCriteriaId
                                                              )
                                                          }
                                                      />
                                                  </td>
                                              </tr>
                                          );
                                      })
                                    : sortedStudents.map((student) => (
                                          <tr
                                              key={student.studentKey}
                                              className={
                                                  student.isShared && student.groupPermission === "VIEW"
                                                      ? "view-only-row"
                                                      : ""
                                              }
                                          >
                                              {items.map((item, index) => {
                                                  const key = cellKey(student.studentKey, item.grade, item.contentId);
                                                  const hasItem = key in ratings;
                                                  const value = ratings[key] || "";
                                                  const isExcluded = value === "EXCLUDED";
                                                  const canEdit = studentEditable(student) && hasItem && !isExcluded;
                                                  let cellControl;
                                                  if (!hasItem) {
                                                      cellControl = <span className="view-only-rating">—</span>;
                                                  } else if (!canEdit) {
                                                      cellControl = (
                                                          <div
                                                              className={classNames(
                                                                  "view-only-rating",
                                                                  getColorClass(value)
                                                              )}
                                                          >
                                                              {RATING_DISPLAY_LABELS[value] || value || "Not Yet Rated"}
                                                          </div>
                                                      );
                                                  } else {
                                                      cellControl = (
                                                          <select
                                                              aria-label={`Rating for ${student.firstName} ${student.lastName} on ${preview(item)}`}
                                                              className={classNames(getColorClass(value))}
                                                              disabled={!ratingsLoaded}
                                                              value={value}
                                                              onChange={(e) => {
                                                                  const val = e.target.value;
                                                                  onRatingChange(
                                                                      student.studentKey,
                                                                      item.grade,
                                                                      item.contentId,
                                                                      val === "__CLEAR__" ? "" : val || ""
                                                                  );
                                                              }}
                                                          >
                                                              <option value="">Select...</option>
                                                              {RATING_OPTIONS.map((opt) => (
                                                                  <option key={opt} value={opt}>
                                                                      {RATING_DISPLAY_LABELS[opt] || opt}
                                                                  </option>
                                                              ))}
                                                              <option value="__CLEAR__">Clear Ratings</option>
                                                          </select>
                                                      );
                                                  }

                                                  return (
                                                      <td
                                                          key={`${student.studentKey}-${item.contentId}`}
                                                          className={
                                                              index === activeColumnIndex ? "active-rating-col" : ""
                                                          }
                                                      >
                                                          <div className="rating-cell">
                                                              {cellControl}
                                                              {hasItem && (
                                                                  <DiffCellCommentIcon
                                                                      canEdit={canEdit && ratingsLoaded}
                                                                      comment={comments?.[key] || ""}
                                                                      contentId={item.contentId}
                                                                      onOpen={() => openCommentModal(student, item)}
                                                                      rating={value}
                                                                      studentKey={student.studentKey}
                                                                  />
                                                              )}
                                                          </div>
                                                      </td>
                                                  );
                                              })}
                                          </tr>
                                      ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="save-btn-ratings">
                <ActionButton
                    disable={!anyStudentEditable || !ratingsLoaded}
                    label="Save"
                    onClick={() => {
                        if (showComments && onSaveSubjectComments) onSaveSubjectComments();
                        else onSave();
                    }}
                />
            </div>

            <CommentModal
                open={commentModalOpen}
                onClose={closeCommentModal}
                onSave={(ctx) => {
                    onSaveComment?.(ctx.studentKey, ctx.grade, ctx.contentId, ctx.comment);
                    closeCommentModal();
                }}
                context={activeCommentContext}
                setContext={setActiveCommentContext}
                isActiveWindow={isActiveWindow}
                mode={mode}
                selectedEpr={selectedEpr}
                userDetails={userDetails}
            />
        </section>
    );
};

DiffRatingGrid.propTypes = {
    actionWindowsDto: PropTypes.array,
    basePath: PropTypes.string,
    comments: PropTypes.object,
    groupData: PropTypes.array,
    hasChanges: PropTypes.bool,
    items: PropTypes.array.isRequired,
    mode: PropTypes.oneOf(["HOMEROOM", "GROUPS"]),
    onBulkRating: PropTypes.func.isRequired,
    onRatingChange: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onSaveComment: PropTypes.func,
    onSaveSubjectComments: PropTypes.func,
    onSubjectCommentChange: PropTypes.func,
    ratings: PropTypes.object.isRequired,
    ratingsLoaded: PropTypes.bool,
    selectedEpr: PropTypes.object,
    selectedSchoolLocationKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedTemplate: PropTypes.object,
    snapshots: PropTypes.object,
    students: PropTypes.array.isRequired,
    subjectComments: PropTypes.object,
    token: PropTypes.string,
    userDetails: PropTypes.object
};

export default DiffRatingGrid;
